import React from "react";
import dellProducts from "../../../data/dell";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

export default function Dell() {
    const pageTitle = "Certified Refurbished Dell Laptops in Bangalore | Credible Technologies";
    const pageDescription = "Buy certified refurbished Dell laptops with warranty. Perfect for students, professionals, and businesses in Bangalore.";

    return (
        <main className="w-full bg-white text-gray-900 overflow-x-hidden">

            {/* =================== SEO HEAD =================== */}
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href="https://www.credibletech.in/refurbished-laptops/dell" />

                {/* Open Graph */}
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.credibletech.in/refurbished-laptops/dell" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
            </Helmet>

            {/* HERO SECTION – DELL BRAND */}
            <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white overflow-hidden">
                <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

                <div className="relative max-w-7xl mx-auto px-4 py-28 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm tracking-wide mb-6">
                        💻 Dell Refurbished Experts
                    </div>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
                        Buy Certified Refurbished <br className="hidden sm:block" />
                        Dell Laptops
                    </h1>
                    <p className="mt-6 text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
                        Up to <span className="text-white font-semibold">3-Year Warranty</span> ·
                        Free Trial Before You Buy ·
                        <span className="text-white font-semibold"> Pay Only If You’re Satisfied</span>
                    </p>
                    <p className="mt-4 text-sm sm:text-base text-gray-400 max-w-2xl mx-auto">
                        Professionally tested Dell laptops with genuine parts,
                        transparent pricing, and expert support in Bangalore since 2017.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
                        <a href="#dell-products" className="bg-white text-black px-8 py-4 rounded-xl font-semibold hover:bg-gray-200 transition">
                            View Available Dell Laptops
                        </a>
                        <a
                            href="https://wa.me/919538577199?text=Hello%2C%20I%20would%20like%20expert%20assistance%20regarding%20Dell%20laptops%20for%20buying%2C%20selling%2C%20or%20service.%20Please%20guide%20me%20with%20the%20best%20options."
                            target="_blank"
                            rel="noreferrer"
                            className="border border-white/40 px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-black transition"
                        >
                            WhatsApp an Expert
                        </a>
                    </div>

                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto text-sm text-gray-300">
                        <div className="bg-white/5 rounded-xl p-5">✅ Certified & Tested Devices</div>
                        <div className="bg-white/5 rounded-xl p-5">🔁 Free Trial & Replacement</div>
                        <div className="bg-white/5 rounded-xl p-5">🛡️ Warranty up to 3 Years</div>
                    </div>
                </div>
            </section>

            {/* WHY CREDIBLE TECHNOLOGIES – PREMIUM VERSION */}
            <section className="relative bg-white overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-40" />
                    <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-100 rounded-full blur-3xl opacity-40" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 py-24">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="inline-block text-xs font-semibold tracking-widest text-blue-600 uppercase mb-4">
                            Trust • Quality • Assurance
                        </span>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
                            Why <span className="text-black">Credible Technologies?</span>
                        </h2>
                        <p className="mt-6 text-gray-600 text-sm sm:text-base leading-relaxed">
                            Buying a refurbished laptop should feel as safe as buying a new one.
                            Here’s how we make that promise real — every single time.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="group bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-blue-50 text-3xl mb-6 group-hover:scale-110 transition">🛡️</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">3 Year Warranty</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                Each laptop passes <strong>42+ quality checks</strong> using genuine parts.
                                That’s why we confidently offer a <strong>3-year on-site warranty</strong> anywhere in India.
                            </p>
                        </div>
                        <div className="group bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-indigo-50 text-3xl mb-6 group-hover:scale-110 transition">🔄</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">20 Days Easy Return</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                Not satisfied? No pressure. <strong>Return or replace within 20 days</strong> — simple, transparent, and stress-free.
                            </p>
                        </div>
                        <div className="group bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-green-50 text-3xl mb-6 group-hover:scale-110 transition">✅</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Pay After Satisfaction</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                First time buying refurbished? <strong>Test the laptop at your place</strong> and pay only when you’re fully confident.
                            </p>
                        </div>
                        <div className="group bg-white border border-gray-200 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-orange-50 text-3xl mb-6 group-hover:scale-110 transition">🤝</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">90+ Channel Partners</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                With <strong>90+ service partners across India</strong>, help is always nearby — wherever your work takes you.
                            </p>
                        </div>
                    </div>

                    <div className="mt-20 text-center">
                        <p className="text-xs sm:text-sm text-gray-500">
                            Trusted by students, professionals, startups & enterprises across India.
                        </p>
                    </div>
                </div>
            </section>

            {/* DELL PRODUCTS SECTION */}
            <section id="dell-products" className="max-w-7xl mx-auto px-4 py-20">
                <div className="text-center mb-14">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">Available Dell Laptops</h2>
                    <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
                        Hand-picked refurbished Dell laptops with warranty, ideal for students, professionals, and businesses.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {dellProducts.map((product) => (
                        <div key={product.id} className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition bg-white flex flex-col">
                            <img src={product.image} alt={product.name} className="w-full h-56 object-cover" />
                            <div className="p-6 flex flex-col flex-grow">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">{product.name}</h3>
                                <ul className="text-sm text-gray-600 space-y-1 mb-5">
                                    {product.specs.slice(0, 3).map((spec, index) => (
                                        <li key={index}>• {spec}</li>
                                    ))}
                                </ul>
                                <div className="mt-auto grid grid-cols-2 gap-3">
                                    <a href={`https://wa.me/919538577199?text=${encodeURIComponent(product.whatsappText)}`} target="_blank" rel="noreferrer" className="text-center bg-black text-white py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 transition">
                                        Enquire
                                    </a>
                                    <Link
                                        to={`/refurbished-laptops/dell/laptops`}
                                        className="text-center border border-black text-black py-3 rounded-xl text-sm font-semibold hover:bg-black hover:text-white transition"
                                    >
                                        View Details
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* BEST PRICE & QUALITY PROMISE*/}
            <section className="relative bg-white overflow-hidden">

                {/* Subtle background accents */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-blue-100/40 blur-3xl rounded-full" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 py-24">

                    {/* Top Content */}
                    <div className="grid lg:grid-cols-2 gap-16 items-center">

                    {/* LEFT CONTENT */}
                    <div>
                        <span className="inline-block text-xs font-semibold tracking-widest text-blue-600 uppercase mb-4">
                        Price • Quality • Confidence
                        </span>

                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                        No One Beats Our <span className="text-black">Prices & Quality</span>
                        </h2>

                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-8 max-w-xl">
                        We challenge the lowest price — <strong>guaranteed.</strong><br />
                        Get premium refurbished laptops backed by a
                        <strong> 3-year warranty</strong> and
                        <strong> 20-day easy return</strong> if quality doesn’t meet your expectations.
                        </p>

                        {/* CORPORATE BOX */}
                        <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Exclusive Bulk Rates for Corporates & Resellers
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Special pricing for bulk purchases. Ideal for
                            <strong> corporates, startups, institutions & resellers</strong>.
                            Get reliable laptops at unbeatable prices.
                        </p>
                        </div>
                    </div>

                    {/* RIGHT CONTENT – FEATURES */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-lg">

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">

                        {/* Feature 1 */}
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-50 text-xl">
                            🌍
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                            Imported Premium Quality
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-green-50 text-xl">
                            💰
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                            Lowest Price Guarantee
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-50 text-xl">
                            🛠️
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                            Reliable After-Sales Support
                            </p>
                        </div>

                        </div>

                        {/* CTA BUTTONS */}
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">

                        {/* CHAT NOW */}
                        <a
                            href="https://wa.me/919538577199?text=Hello%2C%20I%20would%20like%20to%20contact%20Credible%20Technologies%20regarding%20laptops."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-green-500 text-white font-semibold text-sm shadow-md hover:bg-green-600 transition"
                        >
                            CHAT NOW
                        </a>

                        {/* ENQUIRE NOW */}
                        <a
                            href="/contactus#form"
                            className="inline-flex items-center justify-center px-8 py-3 rounded-full border border-gray-900 text-gray-900 font-semibold text-sm hover:bg-gray-900 hover:text-white transition"
                        >
                            ENQUIRE NOW
                        </a>

                        </div>

                    </div>

                    </div>

                </div>
            </section>

            {/* CORPORATE & BUSINESS LAPTOP SOLUTIONS */}
            <section className="relative bg-white">

                {/* Soft background accent */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-20 right-0 w-[500px] h-[500px] bg-blue-100/40 blur-3xl rounded-full" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 py-24">

                    {/* Section Header */}
                    <div className="max-w-3xl mb-16">
                    <span className="inline-block text-xs font-semibold tracking-widest text-blue-600 uppercase mb-4">
                        Business & Corporate Solutions
                    </span>

                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                        Looking for Refurbished Laptops for Your Business?
                    </h2>

                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                        Whether you're a startup, growing enterprise, or large organization,
                        CREDIBLE TECHNOLOGIES delivers reliable, cost-effective refurbished
                        laptops with unmatched support across India.
                    </p>
                    </div>

                    {/* Feature Grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">

                    {/* Feature 1 */}
                    <div className="group border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition">
                        <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-xl bg-blue-50 text-xl">
                        🎯
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        7 Days Free Trial
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                        Try before you buy. Evaluate performance, compatibility, and reliability
                        before making any commitment.
                        </p>
                    </div>

                    {/* Feature 2 */}
                    <div className="group border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition">
                        <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-xl bg-green-50 text-xl">
                        🛡️
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        3-Year Warranty + 20 Days Return
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                        Industry-leading warranty with a hassle-free return and replacement policy.
                        Quality and service guaranteed.
                        </p>
                    </div>

                    {/* Feature 3 */}
                    <div className="group border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition">
                        <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-xl bg-orange-50 text-xl">
                        🏠
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Work From Home Support
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                        Our experts ensure smooth remote operations so your teams can stay productive
                        without technical disruptions.
                        </p>
                    </div>

                    {/* Feature 4 */}
                    <div className="group border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition">
                        <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-xl bg-purple-50 text-xl">
                        🔄
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Alternate Laptop Support
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                        During the warranty period, we provide replacement laptops to ensure zero
                        business downtime.
                        </p>
                    </div>

                    {/* Feature 5 */}
                    <div className="group border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition">
                        <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-xl bg-red-50 text-xl">
                        💰
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Additional Corporate Discounts
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                        Exclusive bulk pricing for startups, enterprises, and resellers.
                        Save more as your business scales.
                        </p>
                    </div>

                    {/* CTA Card */}
                    <div className="border border-gray-900 rounded-2xl p-6 bg-gray-900 text-white flex flex-col justify-between">
                        <div>
                        <h3 className="text-lg font-semibold mb-2">
                            Need a Custom Business Quote?
                        </h3>
                        <p className="text-sm text-gray-300 leading-relaxed">
                            Talk to our sales experts to schedule a demo or discuss bulk requirements.
                        </p>
                        </div>

                        <div className="mt-6 flex flex-col gap-3">
                        <a
                            href="https://wa.me/919538577199?text=Hello%2C%20I%20would%20like%20to%20contact%20Credible%20Technologies%20regarding%20laptops."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-center px-6 py-3 rounded-full bg-green-500 hover:bg-green-600 transition text-sm font-semibold"
                        >
                            Chat with Sales Team
                        </a>

                        <a
                            href="/contactus#form"
                            className="text-center px-6 py-3 rounded-full border border-white text-white hover:bg-white hover:text-gray-900 transition text-sm font-semibold"
                        >
                            Request Corporate Quote
                        </a>
                        </div>
                    </div>

                    </div>
                </div>
            </section>

            {/* EXPLORE REFURBISHED LAPTOP BRANDS (NON-APPLE) */}
            <section className="relative bg-white">

                {/* Subtle background accent */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 left-1/3 w-[500px] h-[500px] bg-blue-100/30 blur-3xl rounded-full" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 py-24">

                    {/* Section Header */}
                    <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
                        Explore Our Refurbished Laptop Brands
                    </h2>
                    <p className="mt-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                        Discover reliable, high-performance refurbished laptops from trusted global brands.
                        Each brand page provides detailed specifications, available models, and instant enquiry options.
                    </p>
                    </div>

                    {/* Brand Cards Grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">

                        {/* ================= APPLE ================= */}
                        <Link
                            to="/refurbished-laptops/apple"
                            className="group border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <img
                                    src="https://cdn.simpleicons.org/apple/000000"
                                    alt="Apple Refurbished Laptops"
                                    className="w-12 h-12"
                                />
                                <h3 className="text-xl font-bold text-gray-900">
                                    Apple Refurbished Laptops
                                </h3>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed mb-6">
                                Apple refurbished laptops are professionally tested, reliable, and perfect for students, professionals, and creatives. Enjoy premium performance with genuine parts and warranty coverage.
                            </p>

                            <span className="inline-flex items-center text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                                View Apple Laptops →
                            </span>
                        </Link>

                        {/* ================= HP ================= */}
                        <Link
                            to="/refurbished-laptops/hp"
                            className="group border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                            <img
                                src="https://cdn.simpleicons.org/hp/000000"
                                alt="HP Refurbished Laptops"
                                className="w-12 h-12"
                            />
                            <h3 className="text-xl font-bold text-gray-900">
                                HP Refurbished Laptops
                            </h3>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed mb-6">
                            HP refurbished laptops deliver a perfect balance of performance, security, and comfort.
                            Ideal for business users, students, and remote professionals.
                            </p>

                            <span className="inline-flex items-center text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                            View HP Laptops →
                            </span>
                        </Link>

                        {/* ================= LENOVO ================= */}
                        <Link
                            to="/refurbished-laptops/lenovo"
                            className="group border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                            <img
                                src="https://cdn.simpleicons.org/lenovo/000000"
                                alt="Lenovo Refurbished Laptops"
                                className="w-12 h-12"
                            />
                            <h3 className="text-xl font-bold text-gray-900">
                                Lenovo Refurbished Laptops
                            </h3>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed mb-6">
                            Known for legendary keyboards and business-class reliability, Lenovo refurbished laptops
                            are perfect for productivity, coding, and enterprise workloads.
                            </p>

                            <span className="inline-flex items-center text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                            View Lenovo Laptops →
                            </span>
                        </Link>

                        {/* ================= ASUS ================= */}
                        <Link
                            to="/refurbished-laptops/asus"
                            className="group border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                            <img
                                src="https://cdn.simpleicons.org/asus/000000"
                                alt="Asus Refurbished Laptops"
                                className="w-12 h-12"
                            />
                            <h3 className="text-xl font-bold text-gray-900">
                                ASUS Refurbished Laptops
                            </h3>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed mb-6">
                            ASUS laptops offer stylish design with powerful internals, making them ideal for
                            creators, students, and multitasking professionals.
                            </p>

                            <span className="inline-flex items-center text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                            View ASUS Laptops →
                            </span>
                        </Link>

                        {/* ================= MSI ================= */}
                        <Link
                            to="/refurbished-laptops/msi"
                            className="group border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                            <img
                                src="https://cdn.simpleicons.org/msi/000000"
                                alt="MSI Refurbished Laptops"
                                className="w-12 h-12"
                            />
                            <h3 className="text-xl font-bold text-gray-900">
                                MSI Refurbished Laptops
                            </h3>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed mb-6">
                            MSI refurbished laptops are designed for gamers and power users, featuring high-refresh
                            displays, dedicated GPUs, and advanced cooling systems.
                            </p>

                            <span className="inline-flex items-center text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                            View MSI Laptops →
                            </span>
                        </Link>

                        {/* ================= ACER ================= */}
                        <Link
                            to="/refurbished-laptops/acer"
                            className="group border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <img
                                    src="https://cdn.simpleicons.org/acer/000000"
                                    alt="Acer Refurbished Laptops"
                                    className="w-12 h-12"
                                />
                                <h3 className="text-xl font-bold text-gray-900">
                                    Acer Refurbished Laptops
                                </h3>
                            </div>

                            <p className="text-sm text-gray-600 leading-relaxed mb-6">
                                Acer refurbished laptops offer excellent value with powerful performance,
                                sleek designs, and reliable build quality. Ideal for students,
                                professionals, and everyday computing needs.
                            </p>

                            <span className="inline-flex items-center text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                                View Acer Laptops →
                            </span>
                        </Link>

                    </div>
                </div>
            </section>

        </main>
    );
}
